import { createSlice } from '@reduxjs/toolkit';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { FRAME_IMAGE_FIELDS, frameImageVariantConfig } from '../../util/cardImageVariants';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { favoriteIdList, mergeSeenSnapshots } from '../../util/favorites';

const { UUID } = sdkTypes;

const RESULT_PAGE_SIZE = 12;
export { RESULT_PAGE_SIZE };

/**
 * 🔴 Story 12.13 (AC-22) — DEFECT #0, a live production defect independent of
 * framing.
 *
 * The third argument to `createImageVariantConfig` is the ASPECT RATIO, applied
 * as `variantHeight = Math.round(aspectRatio * width)` (sdkLoader.js:19). The
 * hardcoded `1` this used to carry therefore asked for 400x400 and 800x800
 * SQUARE files, which are then displayed in a 4:3 card box with
 * `object-fit: cover` (ListingCard.module.css:35-49) — cropping the cover
 * twice. Measured on 90 real cover photos: the median listing showed 56.5 % of
 * its cover where a correct 4:3 variant shows 99.6 %.
 *
 * This was a module CONSTANT with no config access, and `loadData` took no
 * config argument — that plumbing is the only non-trivial part of the fix. It
 * is now a function of the ratio, and `loadData` accepts the standard
 * `(params, search, config)` signature the router already calls every loader
 * with (Routes.js:37).
 *
 * ⚠️ Portrait covers see NO change (the square crop is already the binding
 * one). That is correct and expected — not a failure.
 */
const buildImageFields = (aspectRatio = 3 / 4) => ({
  include: ['images'],
  // 🔴 Story 12.15 — `'publicData'` WHOLE, so `photoFocalPoints` already
  // arrives; no AC-1 allowlist change needed. Pinned anyway in
  // `cardFramingAllowlists.test.js`.
  'fields.listing': ['title', 'price', 'description', 'publicData'],
  'fields.image': ['variants.listing-card', 'variants.listing-card-2x', ...FRAME_IMAGE_FIELDS],
  ...createImageVariantConfig('listing-card', 400, aspectRatio),
  ...createImageVariantConfig('listing-card-2x', 800, aspectRatio),
  ...frameImageVariantConfig(),
  'limit.images': 1,
});

const denormalize = resp => {
  const included = resp.data.included || [];
  const imageMap = {};
  included.forEach(item => {
    if (item.type === 'image') imageMap[item.id.uuid] = item;
  });
  return resp.data.data.map(listing => ({
    ...listing,
    images: (listing.relationships?.images?.data || [])
      .map(ref => imageMap[ref.id.uuid])
      .filter(Boolean),
  }));
};

const favoriteListingsPageSlice = createSlice({
  name: 'FavoriteListingsPage',
  initialState: {
    listings: [],
    // Full saved-id list (privateData.favorites order). The page diffs this
    // against the returned `listings` to find saved IDs that came back with no
    // listing at all — those are the "deleted" bucket.
    favoriteIds: [],
    // Phase 2a: the raw favorites entries (string OR { id, savedAt, ... }) carried
    // through in saved order, so Phase 2b can surface savedAt / lastSeen without a
    // second source. favoriteIds is favoriteIdList(favoriteEntries).
    favoriteEntries: [],
    queryInProgress: false,
    queryError: null,
  },
  reducers: {
    queryListingsRequest: state => {
      state.queryInProgress = true;
      state.queryError = null;
    },
    queryListingsSuccess: (state, action) => {
      state.queryInProgress = false;
      state.listings = action.payload.listings;
      state.favoriteIds = action.payload.favoriteIds;
      state.favoriteEntries = action.payload.favoriteEntries;
    },
    queryListingsError: (state, action) => {
      state.queryInProgress = false;
      state.queryError = action.payload;
    },
  },
});

export const { queryListingsRequest, queryListingsSuccess, queryListingsError } =
  favoriteListingsPageSlice.actions;

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest());
  // AC-22 — derive the variant aspect from config, exactly as
  // SearchPage.duck.js:614-615 and ProfilePage.duck.js:59-60 already do. The
  // fallback is the config DEFAULT (4:3), never the old hardcoded square.
  const { aspectWidth = 4, aspectHeight = 3 } = config?.layout?.listingImage || {};
  const imageFields = buildImageFields(aspectHeight / aspectWidth);

  return dispatch(fetchCurrentUser())
    .then(() => {
      // Raw entries (string OR { id, savedAt, ... }) in saved order. Normalize to
      // an id-string list for the query + Phase-1 diff; carry the full entries
      // through for Phase 2b.
      const favoriteEntries =
        getState().user.currentUser?.attributes?.profile?.privateData?.favorites || [];
      const favoriteIds = favoriteIdList(favoriteEntries);

      if (favoriteIds.length === 0) {
        dispatch(queryListingsSuccess({ listings: [], favoriteIds: [], favoriteEntries: [] }));
        return;
      }

      // NOTE on closed listings: the public Marketplace API (sdk.listings.query)
      // only ever returns *published* (open) listings — closed/cancelled and
      // genuinely-deleted listings are simply absent from the response, with no
      // way to distinguish them via this endpoint. Dropping the previous
      // `states: ['published']` filter does not change that (it was redundant).
      // Sold/on-hold horses stay published (publicData.listingStatus marks them),
      // so they DO come back and are bucketed on the page; any saved ID that
      // returns nothing falls into the page's "deleted" bucket.
      return sdk.listings
        .query({
          ids: favoriteIds.map(id => new UUID(id)),
          perPage: 100,
          ...imageFields,
        })
        .then(response => {
          dispatch(
            queryListingsSuccess({ listings: denormalize(response), favoriteIds, favoriteEntries })
          );
        });
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

/**
 * "Mark seen on load" write-back (Phase 2b). CLIENT-SIDE ONLY — fired once from a
 * useEffect after the listings load, never during SSR. Persists a fresh lastSeen
 * snapshot per favorite so the NEXT visit's change-signals diff against this view.
 *
 * SAFE read-modify-write: re-reads the freshest favorites (fetchCurrentUser),
 * then mergeSeenSnapshots iterates ONLY those (never resurrects an id removed
 * since load), preserves savedAt, refreshes lastSeen for ids whose listing came
 * back, and preserves the existing lastSeen for ids whose listing did not.
 *
 * This updates the baseline for the NEXT visit only — it does NOT touch the page
 * slice (favoriteEntries), so the current view's Updates strip, computed from the
 * AS-LOADED snapshots, is unaffected.
 */
export const markFavoritesSeen = () => (dispatch, getState, sdk) => {
  const listings = getState().FavoriteListingsPage.listings || [];
  if (listings.length === 0) return Promise.resolve();

  const listingsById = new Map(listings.map(l => [l.id.uuid, l]));

  // Re-read freshest favorites before computing the update (a heart toggle may
  // have removed an id between load and now; toggles refetch currentUser).
  return dispatch(fetchCurrentUser())
    .then(() => {
      const fresh =
        getState().user.currentUser?.attributes?.profile?.privateData?.favorites || [];
      if (fresh.length === 0) return null;
      const updated = mergeSeenSnapshots(fresh, listingsById);
      return sdk.currentUser
        .updateProfile({ privateData: { favorites: updated } })
        // enforce: bypass the ~1s currentUser freshness short-circuit so the
        // just-written lastSeen baseline lands in-session, not a stale copy.
        .then(() => dispatch(fetchCurrentUser({ enforce: true })));
    })
    // Best-effort: a failed snapshot write must never surface an error on the
    // page — the view is already rendered from the as-loaded data. But it must
    // leave a trace (we were blind to this failure path before).
    .catch(err => {
      log.error(storableError(err), 'mark-favorites-seen-failed');
      return null;
    });
};

export default favoriteListingsPageSlice.reducer;
